export type QueryNode =
  | { type: 'has'; tagId: string; entityId?: string }
  | { type: 'not'; operand: QueryNode }
  | { type: 'and'; left: QueryNode; right: QueryNode };

export interface AndModifier<TTag extends string = string> {
  not: NotModifier<TTag>;
  has(tagId: TTag): WetagQuery<TTag>;
  hasEntity(entityId: string, tagId: TTag): WetagQuery<TTag>;
}

export interface NotModifier<TTag extends string = string> {
  has(tagId: TTag): WetagQuery<TTag>;
  hasEntity(entityId: string, tagId: TTag | `disabled-${TTag}`): WetagQuery<TTag>;
}

export interface WetagQuery<TTag extends string = string> {
  and: AndModifier<TTag>;

  /** @internal Please don't use this, it's internal @deprecated*/
  _toQueryString(defaultEntityId: string): string;

  /** @internal Please don't use this, it's internal @deprecated*/
  _getNode(): QueryNode;

  _getFirstEntityId(): string | undefined;
}

class QueryBuilder<TTag extends string = string> implements WetagQuery<TTag> {
  constructor(private readonly node: QueryNode) {}

  _getFirstEntityId(): string | undefined {
    const findEntityId = (node: QueryNode): string | undefined => {
      switch (node.type) {
        case 'has':
          return node.entityId;
        case 'not':
          return findEntityId(node.operand);
        case 'and':
          return findEntityId(node.left) ?? findEntityId(node.right);
      }
    };
    return findEntityId(this.node);
  }

  get and(): AndModifier<TTag> {
    return {
      not: {
        has: (tagId: TTag): WetagQuery<TTag> => {
          return new QueryBuilder<TTag>({
            type: 'and',
            left: this.node,
            right: { type: 'not', operand: { type: 'has', tagId } },
          });
        },
        hasEntity: (entityId: string, tagId: TTag): WetagQuery<TTag> => {
          return new QueryBuilder<TTag>({
            type: 'and',
            left: this.node,
            right: { type: 'not', operand: { type: 'has', tagId, entityId } },
          });
        },
      },
      has: (tagId: TTag): WetagQuery<TTag> => {
        return new QueryBuilder<TTag>({
          type: 'and',
          left: this.node,
          right: { type: 'has', tagId },
        });
      },
      hasEntity: (entityId: string, tagId: TTag): WetagQuery<TTag> => {
        return new QueryBuilder<TTag>({
          type: 'and',
          left: this.node,
          right: { type: 'has', tagId, entityId },
        });
      },
    };
  }

  _getNode(): QueryNode {
    return this.node;
  }

  _toQueryString(defaultEntityId: string): string {
    return nodeToQueryString(this.node, defaultEntityId);
  }
}

function nodeToQueryString(node: QueryNode, defaultEntityId: string): string {
  switch (node.type) {
    case 'has': {
      const entityId = node.entityId ?? defaultEntityId;
      return `entity_has("${entityId}", "${node.tagId}")`;
    }
    case 'not':
      return `NOT ${nodeToQueryString(node.operand, defaultEntityId)}`;
    case 'and':
      return `${nodeToQueryString(node.left, defaultEntityId)} AND ${nodeToQueryString(node.right, defaultEntityId)}`;
  }
}

export function has<TTag extends string>(tagId: TTag): WetagQuery<TTag> {
  return new QueryBuilder<TTag>({ type: 'has', tagId });
}

export function hasEntity<TTag extends string>(entityId: string, tagId: TTag | `disabled-${TTag}`): WetagQuery<TTag> {
  return new QueryBuilder<TTag>({ type: 'has', tagId, entityId });
}

export function isWetagQuery(value: unknown): value is WetagQuery {
  return (
    typeof value === 'object' &&
    value !== null &&
    '_toQueryString' in value &&
    typeof (value as WetagQuery)._toQueryString === 'function'
  );
}

export function normalizeToQuery<TTag extends string>(input: TTag | WetagQuery<TTag>): WetagQuery<TTag> {
  if (isWetagQuery(input)) {
    return input;
  }
  return has(input).and.not.has(`disable-${input}` as TTag);
}

export function normalizeToEntityQuery<TTag extends string>(
  tag: string,
  input: TTag | WetagQuery<TTag>
): WetagQuery<TTag> {
  if (isWetagQuery(input)) {
    return input;
  }
  return hasEntity(input, tag).and.not.hasEntity(input, `disable-${tag}` as TTag);
}

export function getQueryCacheKey(query: WetagQuery): string {
  return serializeNode(query._getNode());
}

function serializeNode(node: QueryNode): string {
  switch (node.type) {
    case 'has':
      return node.entityId ? `has:${node.entityId}:${node.tagId}` : `has:${node.tagId}`;
    case 'not':
      return `not(${serializeNode(node.operand)})`;
    case 'and':
      return `and(${serializeNode(node.left)},${serializeNode(node.right)})`;
  }
}
